import { Building2, ExternalLink, Settings } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useMe } from "@/features/members/hooks/useMe";
import {
  useContributions,
  useMemberProfile,
} from "@/features/members/hooks/useMemberProfile";
import { DetailSkeleton } from "@/components/Skeletons";
import { PreviewBanner } from "@/components/PreviewCutoff";
import { sectionTitleClass } from "@/lib/typography";
import { videoEmbedUrl } from "@/lib/videoEmbed";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export default function MemberProfilePage() {
  const { id } = useParams();
  const { data: member, isPending, error } = useMemberProfile(id);
  const { data: contributions } = useContributions(id);
  const { data: me } = useMe();
  const isAdmin = me?.member?.role === "admin";

  if (isPending) return <DetailSkeleton />;
  if (error || !member) {
    return (
      <p className="text-muted-foreground mt-12 text-center">
        This member profile isn&apos;t available.
      </p>
    );
  }

  return (
    <div className="animate-page-enter max-w-3xl">
      {/* No "back to directory" link: the sidebar is the way back, and every
          page carrying its own one duplicated it. */}
      <div className="flex flex-wrap items-center gap-4">
        <Avatar className="size-20">
          <AvatarImage src={member.image_url ?? undefined} alt={member.name} />
          <AvatarFallback className="text-2xl">
            {member.name.charAt(0) || "\u00b7"}
          </AvatarFallback>
        </Avatar>
        <div>
          {/* The name is withheld in the preview, and the heading rendered
              empty, so the organization underneath read as the title and
              nothing said a name was missing (June, 2026-09-24). The same bar
              the directory card stands in its place, at heading size. */}
          <h1 className="text-3xl">
            {member.name || (
              <span
                aria-label="Name hidden. Names are shown to members."
                className="bg-muted-foreground/25 my-1.5 block h-5 w-56 max-w-full rounded-sm"
              />
            )}
          </h1>
          <p className="text-muted-foreground">
            {[member.title, member.org].filter(Boolean).join(", ")}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {member.location && (
              <span className="text-muted-foreground text-sm">
                {member.location}
              </span>
            )}
          </div>
          {/* No tier badge here on purpose: whether someone pays, was gifted a
              membership, or is on the free tier is nobody else's business. */}
        </div>
        {/* The gear is the way to everything an administrator does for a
            member, profile text included: one page, also reached from the
            Members table (June, 2026-10-06). */}
        {isAdmin && (
          <Button asChild variant="outline" size="icon-sm" className="ml-auto">
            <Link
              to={`/admin/members/${member.id}`}
              aria-label="Manage membership"
              title="Manage membership"
            >
              <Settings />
            </Link>
          </Button>
        )}
      </div>

      {/* The directory's own banner, so the preview says the same thing the
          same way in both places, and carries the offer with it: a reader who
          followed a card here had nothing to do but go back (June,
          2026-09-24). */}
      <PreviewBanner show={Boolean(member.preview)}>
        Bio, mediums, links and contact details are available to members only.
      </PreviewBanner>

      {(member.mediums ?? []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(member.mediums ?? []).map((k) => (
            <Badge key={k} variant="outline">
              {k}
            </Badge>
          ))}
        </div>
      )}

      {member.bio && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>About</CardTitle>
          </CardHeader>
          <CardContent className="text-sm leading-relaxed whitespace-pre-wrap">
            {member.bio}
          </CardContent>
        </Card>
      )}

      {!member.preview && (member.directory_email || member.email) && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Contact</CardTitle>
          </CardHeader>
          <CardContent>
            {/* The address they chose to publish, else the account one — a
                full member is promised contact details, which is why the
                registration form asks. */}
            <a
              href={`mailto:${member.directory_email || member.email}`}
              className="underline underline-offset-4"
            >
              {member.directory_email || member.email}
            </a>
          </CardContent>
        </Card>
      )}
      {member.organizations.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Organizations</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Names only, each a link: the legacy's own page carries its
                description, website, mediums and stewards. */}
            <ul className="flex flex-wrap gap-2">
              {member.organizations.map((o) => (
                <li key={o.id}>
                  {/* A button, not a hyperlink: the page it opens is the whole
                      point of the card, and black underlined text did not read
                      as somewhere to go. */}
                  <Button asChild variant="outline">
                    <Link to={`/organizations/${o.id}`}>
                      <Building2 data-slot="icon" />
                      {o.name}
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {member.ip && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>
              Intellectual property
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm leading-relaxed whitespace-pre-wrap">
            {member.ip}
          </CardContent>
        </Card>
      )}

      {(member.links ?? []).length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(member.links ?? []).map((l, i) => (
              <a
                key={i}
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-sm underline-offset-4 hover:underline"
              >
                <ExternalLink className="size-3.5" /> {l.label || l.url}
              </a>
            ))}
          </CardContent>
        </Card>
      )}

      {contributions && contributions.resources.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>
              Contributed resources
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {contributions.resources.map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-sm">
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline-offset-4 hover:underline"
                >
                  {r.title}
                </a>
                <Badge variant="secondary" className="text-xs">
                  {r.category}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {contributions && contributions.threads.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>
              Discussions started
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {contributions.threads.map((t) => (
              <div key={t.id} className="text-sm">
                <Link
                  to={`/discussions/${t.id}`}
                  className="underline-offset-4 hover:underline"
                >
                  {t.title}
                </Link>
                <span className="text-muted-foreground">
                  {" "}
                  · {t.reply_count} replies
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(member.media ?? []).length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Media</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {(member.media ?? []).map((m, i) => (
              <figure key={i} className="space-y-1.5">
                {m.type === "image" && (
                  <img
                    src={m.url}
                    alt={m.title ?? "Member media"}
                    className="aspect-video w-full rounded-md object-cover"
                  />
                )}
                {m.type === "audio" && (
                  <audio controls src={m.url} className="w-full" />
                )}
                {m.type === "video" && <ProfileVideo url={m.url} title={m.title} />}
                {m.title && (
                  <figcaption className="text-muted-foreground text-xs">
                    {m.title}
                  </figcaption>
                )}
              </figure>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** A Vimeo or YouTube link in that site's player; anything else as a file. */
function ProfileVideo({ url, title }: { url: string; title?: string }) {
  const embed = videoEmbedUrl(url);
  if (!embed) return <video controls src={url} className="w-full rounded-md" />;
  return (
    <iframe
      src={embed}
      title={title || "Member video"}
      className="aspect-video w-full rounded-md border-0"
      allow="fullscreen; picture-in-picture; encrypted-media"
      allowFullScreen
      loading="lazy"
      // YouTube refuses to play with no referrer at all (error 153).
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
}
